"use client";

import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import * as React from "react";
import styled, { useTheme } from "styled-components";

import { IconButton } from "../../../slate-deprecated/IconButton";
import { Chevron } from "../../icons/foundation/Chevron";
import { Close } from "../../icons/foundation/Close";
import { getSpacing } from "../../spacing";

import { ModalNavigationProps } from "./ModalNavigationProps";

export const ModalNavigationAbsolute: React.FC<ModalNavigationProps> = ({
  onClose,
  onBack,
  onForward,
  primary,
  showCloseButton,
  closeButtonVariant,
  closeIconProps,
  backIconProps,
  forwardIconProps,
}) => {
  const theme = useTheme();
  const modalTheme = theme.modals?.[primary ? "primary" : "default"];
  const iconColor = modalTheme?.color ?? Core.icon.default;
  const iconProps = modalTheme?.iconProps ?? {};
  const { strictLocalize } = useStrictLocalization();

  const closeText: string = strictLocalize({
    id: "sqyFFG",
    defaultMessage: "Close",
    description: {
      text: "alt text for screen reader for the close icon button",
    },
  });

  const previousText: string = strictLocalize({
    id: "E8zXjQ",
    defaultMessage: "Previous",
    description: {
      text: "alt text for screen reader for the < icon button",
    },
  });

  const nextText: string = strictLocalize({
    id: "t+MKgo",
    defaultMessage: "Next",
    description: {
      text: "alt text for screen reader for the > icon button",
    },
  });

  return (
    <>
      {showCloseButton && onClose ? (
        <CloseButton
          aria-label={closeText}
          className="closeButton"
          onClick={onClose}
          data-test-id="modalCloseButton"
          variant={closeButtonVariant ?? "tertiary"}
          style={{
            margin:
              closeButtonVariant === "secondary" ? getSpacing("2x") : undefined,
          }}
        >
          <Close color={iconColor} {...iconProps} {...closeIconProps} />
        </CloseButton>
      ) : null}
      {onBack ? (
        <BackButton
          aria-label={previousText}
          className="backButton"
          onClick={onBack}
          data-test-id="modalBackButton"
          variant="tertiary"
        >
          <Chevron
            direction="left"
            color={iconColor}
            {...iconProps}
            {...backIconProps}
          />
        </BackButton>
      ) : null}
      {onForward ? (
        <ForwardButton
          aria-label={nextText}
          className="forwardButton"
          onClick={onForward}
          data-test-id="modalForwardButton"
          variant="tertiary"
        >
          <Chevron
            direction="right"
            color={iconColor}
            {...iconProps}
            {...forwardIconProps}
          />
        </ForwardButton>
      ) : null}
    </>
  );
};

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
`;

const BackButton = styled(IconButton)`
  left: 0;
  position: absolute;
  top: 0;
  z-index: 1;
`;

const ForwardButton = styled(IconButton)`
  left: 64px;
  position: absolute;
  top: 0;
  z-index: 1;
`;
